export interface SearchableItem {
  [key: string]: any;
}

export interface SearchConfig {
  searchTerm: string;
  fields: string[];
}

export function filterBySearch<T extends SearchableItem>(
  items: T[],
  searchTerm: string,
  fields: string[],
): T[] {
  if (!searchTerm.trim()) {
    return items;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  return items.filter(item => {
    return fields.some(fieldPath => {
      const value = getNestedValue(item, fieldPath);
      const normalizedValue = value?.toString().toLowerCase() || '';
      return normalizedValue.includes(normalizedSearchTerm);
    });
  });
}

function getNestedValue(obj: any, path: string): any {
  const pathParts = path.split('.');
  let current = obj;

  for (let i = 0; i < pathParts.length; i++) {
    const key = pathParts[i];

    if (!current) return;

    if (Array.isArray(current)) {
      const remainingPath = pathParts.slice(i).join('.');
      const arrayResults = current
        .map(item => {
          if (!item || typeof item !== 'object') return undefined;
          return getNestedValue(item, remainingPath);
        })
        .filter(value => value !== undefined && value !== null);

      return arrayResults.length > 0 ? arrayResults.join(' ') : undefined;
    }

    current = current[key];
  }

  return current;
}

export function filterSharedEquipment<T extends SearchableItem>(
  equipment: T[],
  searchTerm: string,
): T[] {
  const searchFields = [
    'shareData.sharedItemDetails.sharedItemLabel',
    'shareData.sharedItemDetails.ownerName',
    'shareData.sharedItemDetails.ownerEmail',
    'nickName',
    'model',
    'pinOrSerial',
    'serial',
  ];

  return filterBySearch(equipment, searchTerm, searchFields);
}

export function filterSharedFleets<T extends SearchableItem>(
  fleets: T[],
  searchTerm: string,
): T[] {
  const searchFields = [
    'sharedItemDetails.sharedItemLabel',
    'sharedItemDetails.ownerName',
    'sharedItemDetails.ownerEmail',
  ];

  return filterBySearch(fleets, searchTerm, searchFields);
}

export function filterSharedWith<T extends SearchableItem>(
  sharedWith: T[],
  searchTerm: string,
): T[] {
  const searchFields = [
    'displayName',
    'email',
    'sharedEquipments.sharedItemDetails.sharedItemLabel',
    'sharedFleets.sharedItemDetails.sharedItemLabel',
  ];

  return filterBySearch(sharedWith, searchTerm, searchFields);
}
